import React, { useState, useEffect } from 'react';
import {
    Plus,
    Calendar,
    Loader2,
    MessageSquare,
    ToggleLeft,
    ToggleRight,
    X,
    Save,
    Trash2,
    XCircle,
    PlusCircle,
    Search,     // Added
    Download,   // Added
    FileSpreadsheet // Added
} from 'lucide-react';
import { apiFetch } from '../api';

export default function SmsTemplateManager() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewActive, setViewActive] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // --- New State for Search ---
    const [searchQuery, setSearchQuery] = useState('');

    // --- API Calls ---
    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const response = await apiFetch(`/get/all/sms/template?active=${viewActive}`, {
                method: 'GET'
            });
            const data = await response.json();

            if (data.ERROR === 'SUCCESS') {
                setTemplates(data.DATA || []);
            } else {
                console.error('Failed to fetch:', data.message);
                setTemplates([]);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    // --- FIX: Logic corrected to use 'ACTIVE' (uppercase) ---
    const handleStatusUpdate = async (id, currentStatus) => {
        try {
            // If currentStatus is missing or undefined, default to false so we switch to true
            const statusBool = currentStatus === true || currentStatus === 'true';
            const newStatus = !statusBool;

            const response = await apiFetch(`/update/sms/template/status?active=${newStatus}&id=${id}`, {
                method: 'PUT'
            });
            const data = await response.json();

            if (data.ERROR === 'SUCCESS') {
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    useEffect(() => {
        fetchTemplates();
        // Reset search when switching tabs
        setSearchQuery('');
    }, [viewActive]);

    // --- Search & Filter Logic ---
    const filteredTemplates = templates.filter(t =>
        t.smsTemplate && t.smsTemplate.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // --- Export Logic (CSV) ---
    const handleExport = () => {
        if (!filteredTemplates.length) return;

        // Define headers
        const headers = ["ID", "Content", "Date", "Status"];

        // Map data to rows
        const rows = filteredTemplates.map(t => [
            t._id,
            `"${t.smsTemplate.replace(/"/g, '""')}"`, // Escape quotes for CSV
            t.creation_time_str || '',
            t.ACTIVE ? "Active" : "Inactive"
        ]);

        // Join to CSV string
        const csvContent = [
            headers.join(","),
            ...rows.map(e => e.join(","))
        ].join("\n");

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `sms_templates_${viewActive ? 'active' : 'inactive'}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 p-6 md:p-8 font-sans">

            {/* Header Section */}
            <div className="max-w-6xl mx-auto mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                        <MessageSquare className="w-8 h-8 text-indigo-500" />
                        SMS Templates
                    </h1>
                    <p className="text-slate-400">Manage your approved SMS templates</p>
                </div>

                <div className="flex items-center gap-3 bg-slate-900/50 p-1.5 rounded-xl border border-white/10">
                    <button
                        onClick={() => setViewActive(true)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewActive
                            ? 'bg-indigo-500 text-white shadow-lg'
                            : 'text-slate-400 hover:text-white'
                            }`}
                    >
                        Active
                    </button>
                    <button
                        onClick={() => setViewActive(false)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${!viewActive
                            ? 'bg-slate-700 text-white shadow-lg'
                            : 'text-slate-400 hover:text-white'
                            }`}
                    >
                        Inactive
                    </button>
                </div>

                <div className="flex gap-2">
                    {/* New Export Button */}
                    <button
                        onClick={handleExport}
                        disabled={filteredTemplates.length === 0}
                        className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2.5 rounded-xl font-medium border border-white/10 flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Export filtered list to Excel/CSV"
                    >
                        <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                        <span className="hidden sm:inline">Export</span>
                    </button>

                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-5 py-2.5 rounded-xl font-medium shadow-lg shadow-indigo-500/20 flex items-center gap-2 transition-all"
                    >
                        <Plus className="w-5 h-5" />
                        <span className="hidden sm:inline">Add Template</span>
                        <span className="sm:hidden">Add</span>
                    </button>
                </div>
            </div>

            {/* Search & Stats Bar */}
            <div className="max-w-6xl mx-auto mb-8 bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col sm:flex-row gap-4 justify-between items-center backdrop-blur-sm">

                {/* Search Input */}
                <div className="relative w-full sm:max-w-md group">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-400 transition-colors" />
                    <input
                        type="text"
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
                    />
                </div>

                {/* Count Badge */}
                <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-900/50 px-3 py-1.5 rounded-lg border border-white/5">
                    <span>Total Found:</span>
                    <span className="text-white font-mono font-bold">{filteredTemplates.length}</span>
                </div>
            </div>

            {/* Content Grid */}
            <div className="max-w-6xl mx-auto">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                    </div>
                ) : filteredTemplates.length === 0 ? (
                    <div className="text-center py-20 bg-slate-900/30 rounded-2xl border border-white/5 border-dashed animate-in fade-in duration-500">
                        {searchQuery ? (
                            <>
                                <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-slate-300">No matching templates</h3>
                                <p className="text-slate-500 mt-2">
                                    No templates found matching "{searchQuery}"
                                </p>
                            </>
                        ) : (
                            <>
                                <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-slate-300">No templates found</h3>
                                <p className="text-slate-500 mt-2">
                                    There are no {viewActive ? 'active' : 'inactive'} templates at the moment.
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredTemplates.map((template) => (
                            <TemplateCard
                                key={template._id}
                                data={template}
                                // Fix: Passing template.ACTIVE (case sensitive from backend)
                                onToggle={() => handleStatusUpdate(template._id, template.ACTIVE)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Add Modal */}
            <AddTemplateModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={() => {
                    setIsAddModalOpen(false);
                    setViewActive(true);
                    fetchTemplates();
                }}
            />
        </div>
    );
}

// --- Sub Component: Template Card ---
function TemplateCard({ data, onToggle }) {
    return (
        <div className="group relative bg-white/5 hover:bg-white/10 border border-white/10 hover:border-indigo-500/30 rounded-2xl p-5 transition-all duration-300 backdrop-blur-sm flex flex-col justify-between h-full animate-in fade-in zoom-in duration-300">

            <div className="mb-4">
                <div className="flex justify-between items-start mb-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-800 text-slate-300 border border-white/10 flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        {data.creation_time_str || 'Unknown Date'}
                    </span>
                    <button
                        onClick={onToggle}
                        className="text-slate-400 hover:text-white transition-colors focus:outline-none"
                        title={data.ACTIVE ? "Deactivate" : "Activate"}
                    >
                        {data.ACTIVE ? (
                            <ToggleRight className="w-8 h-8 text-emerald-400" />
                        ) : (
                            <ToggleLeft className="w-8 h-8 text-slate-500" />
                        )}
                    </button>
                </div>

                <p className="text-slate-200 text-sm leading-relaxed font-mono bg-black/20 p-3 rounded-lg border border-white/5 break-words">
                    {data.smsTemplate}
                </p>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className={`w-2 h-2 rounded-full ${data.ACTIVE ? 'bg-emerald-500' : 'bg-slate-500'}`}></span>
                    <span className="font-mono text-slate-400 truncate max-w-[150px]" title={data._id}>
                        {data._id}
                    </span>
                </div>
            </div>
        </div>
    );
}

// --- Sub Component: Add Modal (Unchanged from previous fix) ---
function AddTemplateModal({ isOpen, onClose, onSuccess }) {
    const [inputs, setInputs] = useState([{ id: Date.now(), value: '' }]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setInputs([{ id: Date.now(), value: '' }]);
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleInputChange = (id, newValue) => {
        setInputs(prev => prev.map(item =>
            item.id === id ? { ...item, value: newValue } : item
        ));
        setError('');
    };

    const handleAddInput = () => {
        setInputs(prev => [...prev, { id: Date.now(), value: '' }]);
    };

    const handleRemoveInput = (id) => {
        if (inputs.length === 1) {
            handleInputChange(id, '');
            return;
        }
        setInputs(prev => prev.filter(item => item.id !== id));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validTemplates = inputs.map(i => i.value.trim()).filter(val => val.length > 0);

        if (validTemplates.length === 0) {
            setError("Please enter at least one template content");
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const payload = { smsTemplate: validTemplates };
            const response = await apiFetch('/add/sms/template', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.ERROR === 'SUCCESS') {
                onSuccess();
            } else {
                setError(data.message || 'Failed to add templates');
            }
        } catch (err) {
            console.error(err);
            setError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
            <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-white/10 max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh]">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/10 opacity-50 pointer-events-none" />
                <div className="relative p-6 pb-2 shrink-0">
                    <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10"><X className="w-5 h-5" /></button>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg shadow-indigo-500/20 shrink-0"><MessageSquare className="w-6 h-6 text-white" /></div>
                        <div><h3 className="text-xl font-bold text-white">Add New Templates</h3><p className="text-white/60 text-sm">Create one or more SMS templates.</p></div>
                    </div>
                </div>
                <div className="relative p-6 pt-2 overflow-y-auto custom-scrollbar flex-1">
                    <form id="add-template-form" onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-white/80">Template Content</label>
                            {inputs.map((input, index) => (
                                <div key={input.id} className="relative group animate-in fade-in slide-in-from-bottom-2 duration-300">
                                    <div className="relative">
                                        <textarea value={input.value} onChange={(e) => handleInputChange(input.id, e.target.value)} placeholder={`Enter content for template ${index + 1}...`} rows={2} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all resize-none pr-10" disabled={isSubmitting} />
                                        {(inputs.length > 1) && (<button type="button" onClick={() => handleRemoveInput(input.id)} className="absolute top-2 right-2 p-1.5 text-white/20 hover:text-red-400 hover:bg-white/5 rounded-lg transition-all"><Trash2 className="w-4 h-4" /></button>)}
                                    </div>
                                </div>
                            ))}
                            <button type="button" onClick={handleAddInput} disabled={isSubmitting} className="w-full py-2 border-2 border-dashed border-white/10 rounded-xl text-white/50 hover:text-indigo-400 hover:border-indigo-500/30 hover:bg-white/5 transition-all text-sm font-medium flex items-center justify-center gap-2"><PlusCircle className="w-4 h-4" /> Add Another Template</button>
                        </div>
                        {error && (<div className="flex items-start space-x-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"><XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" /><span className="text-sm text-red-200">{error}</span></div>)}
                    </form>
                </div>
                <div className="relative p-6 pt-0 shrink-0 bg-gradient-to-t from-slate-900 to-transparent">
                    <div className="flex space-x-3 pt-4 border-t border-white/5">
                        <button type="button" onClick={onClose} disabled={isSubmitting} className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-medium transition-all duration-200 border border-white/10 disabled:opacity-50">Cancel</button>
                        <button type="submit" form="add-template-form" disabled={isSubmitting} className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-xl font-medium shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">{isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /><span>Saving...</span></>) : (<><Save className="w-5 h-5" /><span>Save All ({inputs.filter(i => i.value.trim()).length})</span></>)}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}